import * as React from 'react';
import { useTranslations } from 'next-intl';
import { cn } from '@/shared/lib/cn';

interface SkeletonProps extends React.HTMLAttributes<HTMLDivElement> {
  variant?: 'text' | 'rectangular' | 'circular';
  width?: string | number;
  height?: string | number;
  animation?: 'pulse' | 'wave' | 'none';
}

function Skeleton({
  className,
  variant = 'text',
  width,
  height,
  animation = 'pulse',
  ...props
}: SkeletonProps) {
  const t = useTranslations('common');
  const loadingLabel = t('loading');
  const baseClasses =
    animation === 'wave'
      ? 'bg-gradient-to-r from-muted via-muted/70 to-muted bg-[length:200%_100%]'
      : 'bg-muted';

  const animationClasses = {
    pulse: 'animate-pulse',
    wave: 'animate-shimmer',
    none: '',
  };

  const variantClasses = {
    text: 'rounded',
    rectangular: 'rounded-md',
    circular: 'rounded-full',
  };

  const style: React.CSSProperties = {};
  if (width) style.width = typeof width === 'number' ? `${width}px` : width;
  if (height)
    style.height = typeof height === 'number' ? `${height}px` : height;

  return (
    <div
      className={cn(
        baseClasses,
        animationClasses[animation],
        variantClasses[variant],
        className
      )}
      style={style}
      aria-label={loadingLabel}
      role="status"
      {...props}
    >
      <span className="sr-only">{loadingLabel}</span>
    </div>
  );
}

type SkeletonCardProps = React.HTMLAttributes<HTMLDivElement>;

function SkeletonCard({ className, ...props }: SkeletonCardProps) {
  return (
    <div
      className={cn('bg-card rounded-lg shadow-md p-6', className)}
      {...props}
    >
      <Skeleton variant="rectangular" height={200} className="mb-4" />
      <Skeleton variant="text" height={24} className="mb-2" />
      <Skeleton variant="text" height={16} width="80%" />
    </div>
  );
}

interface SkeletonListProps extends React.HTMLAttributes<HTMLDivElement> {
  count?: number;
}

function SkeletonList({ count = 3, className, ...props }: SkeletonListProps) {
  return (
    <div className={className} {...props}>
      {Array.from({ length: count }).map((_, index) => (
        <div key={index} className="mb-4 p-4 bg-card rounded-lg shadow">
          <div className="flex items-center space-x-4">
            <Skeleton variant="circular" width={48} height={48} />
            <div className="flex-1">
              <Skeleton variant="text" height={20} className="mb-2" />
              <Skeleton variant="text" height={16} width="60%" />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

export { Skeleton, SkeletonCard, SkeletonList };
